import React, { useEffect, useState, useMemo } from 'react';
import { Box, Paper, Checkbox, FormControlLabel, Button, useTheme, Grid, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useCreateOrgStructureAssignment } from '../../hooks/orgStructureAssignments/useCreateOrgStructureAssignment';
import { useUpdateOrgStructureAssignment } from '../../hooks/orgStructureAssignments/useUpdateOrgStructureAssignment';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { useGetAcademicYears } from '../../hooks/academicYears/useGetAcademicYears';
import { useGetAcademicTerms } from '../../hooks/academicTerms/useGetAcademicTerms';
import { useGetOrgStructureItemsTree } from '../../hooks/orgStructureItems/useGetOrgStructureItemsTree';
import { useGetDepartments } from '../../hooks/departments/useGetDepartments';
import { getEmployeesForSelect } from '../../api/apiEmployees';
import { useCurrentContext } from '../../hooks/useCurrentContext';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';
import i18next from 'i18next';

const FormAddEditOrgStructureAssignment = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const isArabic = i18n.language === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { createAssignment, isPending: isCreating } = useCreateOrgStructureAssignment();
    const { editAssignment, isUpdate } = useUpdateOrgStructureAssignment();
    const { isLoading: schoolsLoading, schools } = useGetSchools(1, 999);
    const { academicYears } = useGetAcademicYears(1, 999);
    const { academicTerms } = useGetAcademicTerms(1, 999);
    const { treeItems: orgStructureItems } = useGetOrgStructureItemsTree();
    const { departments } = useGetDepartments(1, 999);
    const { currentSchoolId, currentYearId } = useCurrentContext();

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        school_id: '', academic_year_id: '', academic_term_id: '',
        organizational_structure_item_id: '', department_id: '', employee_id: '',
        starts_at: '', ends_at: '', is_active: true, notes: '',
    });

    // Auto-set current context for new records
    useEffect(() => {
        if (!isEdit) {
            setForm((p) => ({
                ...p,
                school_id: p.school_id || currentSchoolId || '',
                academic_year_id: p.academic_year_id || currentYearId || '',
            }));
        }
    }, [currentSchoolId, currentYearId, isEdit]);

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            school_id: currentData.school_id || '',
            academic_year_id: currentData.academic_year_id || '',
            academic_term_id: currentData.academic_term_id || '',
            organizational_structure_item_id: currentData.organizational_structure_item_id || '',
            department_id: '',
            employee_id: currentData.employee_id || '',
            starts_at: currentData.starts_at || '',
            ends_at: currentData.ends_at || '',
            is_active: currentData.is_active ?? true,
            notes: currentData.notes || '',
        });
    }, [isEdit, currentData]);

    // Smart employee loading with job_title + exclusion filters
    const empFilters = useMemo(() => ({
        school_id: form.school_id || undefined,
        department_id: form.department_id || undefined,
        exclude_assigned_item_id: (!isEdit && form.organizational_structure_item_id) || undefined,
        exclude_assigned_school_id: (!isEdit && form.school_id) || undefined,
        exclude_assigned_year_id: (!isEdit && form.academic_year_id) || undefined,
    }), [form.school_id, form.department_id, form.organizational_structure_item_id, form.academic_year_id, isEdit]);

    const { data: empSelectData } = useQuery({
        queryKey: ["employeesForSelect", empFilters, i18next.language],
        queryFn: () => getEmployeesForSelect(empFilters),
        enabled: !!form.school_id,
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') { setForm((p) => ({ ...p, [name]: checked })); return; }
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            school_id: Number(form.school_id),
            academic_year_id: Number(form.academic_year_id),
            academic_term_id: form.academic_term_id ? Number(form.academic_term_id) : null,
            organizational_structure_item_id: Number(form.organizational_structure_item_id),
            employee_id: Number(form.employee_id),
            starts_at: form.starts_at || null,
            ends_at: form.ends_at || null,
            is_active: !!form.is_active,
            notes: form.notes || null,
        };
        if (isEdit && currentData?.id) { editAssignment({ req: payload, id: currentData.id }); }
        else { createAssignment(payload); }
    };

    if (schoolsLoading) return <LoadingPage />;

    const schoolOpts = (schools || []).map((s) => ({ id: s.id, name: s.name }));
    const yearOpts = (academicYears || [])
        .filter((y) => !form.school_id || y.school_id === Number(form.school_id))
        .map((y) => ({ id: y.id, name: y.name }));
    const termOpts = (academicTerms || [])
        .filter((t) => !form.academic_year_id || t.academic_year_id === Number(form.academic_year_id))
        .map((t) => ({ id: t.id, name: t.name }));
    const itemOpts = (orgStructureItems || []).map((i) => ({ id: i.id, name: i.name }));
    const deptOpts = [
        { id: '', name: isArabic ? 'الكل' : 'All' },
        ...(departments || [])
            .filter((d) => !form.school_id || d.school_id === Number(form.school_id))
            .map((d) => ({ id: d.id, name: d.name })),
    ];

    // for-select returns [{ value, label }]
    const empData = empSelectData?.data || [];
    const empOpts = Array.isArray(empData)
        ? empData.map((e) => ({ id: e.value, name: e.label }))
        : [];

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                {isArabic
                    ? 'هذا سجل تعيين فعلي لموظف في منصب تنظيمي. عناصر الهيكل (المناصب) تُعرَّف في صفحة "الهيكل التنظيمي".'
                    : 'This is an actual assignment of an employee to a position. Position definitions are managed in "Org Structure Items".'}
            </Alert>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المدرسة' : 'School'} type="select" name="school_id"
                            value={form.school_id}
                            onChange={(e) => setForm((p) => ({ ...p, school_id: e.target.value, academic_year_id: '', academic_term_id: '', employee_id: '' }))}
                            options={schoolOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'السنة الدراسية' : 'Academic Year'} type="select" name="academic_year_id"
                            value={form.academic_year_id}
                            onChange={(e) => setForm((p) => ({ ...p, academic_year_id: e.target.value, academic_term_id: '' }))}
                            options={yearOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الفصل الدراسي (اختياري)' : 'Academic Term (optional)'} type="select" name="academic_term_id"
                            value={form.academic_term_id}
                            onChange={(e) => setForm((p) => ({ ...p, academic_term_id: e.target.value }))}
                            options={[{ id: '', name: isArabic ? 'بدون فصل' : 'None' }, ...termOpts]} required={false} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المنصب التنظيمي' : 'Position'} type="select" name="organizational_structure_item_id"
                            value={form.organizational_structure_item_id}
                            onChange={(e) => setForm((p) => ({ ...p, organizational_structure_item_id: e.target.value, employee_id: '' }))}
                            options={itemOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'القسم' : 'Department'} type="select" name="department_id"
                            value={form.department_id}
                            onChange={(e) => setForm((p) => ({ ...p, department_id: e.target.value, employee_id: '' }))}
                            options={deptOpts} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الموظف' : 'Employee'} type="select" name="employee_id"
                            value={form.employee_id}
                            onChange={(e) => setForm((p) => ({ ...p, employee_id: e.target.value }))}
                            options={empOpts}
                            disabled={!form.school_id} />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'تاريخ البداية' : 'Starts At'} type="date" name="starts_at"
                            value={form.starts_at} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'تاريخ النهاية' : 'Ends At'} type="date" name="ends_at"
                            value={form.ends_at} onChange={handleChange} required={false} min={form.starts_at} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <FormControlLabel
                                control={<Checkbox checked={form.is_active} name="is_active" onChange={handleChange} size="small" />}
                                label={isArabic ? 'فعّال' : 'Active'} />
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <FormInput label={isArabic ? 'ملاحظات' : 'Notes'}
                            placeholder={isArabic ? 'ملاحظات إضافية...' : 'Additional notes...'}
                            type="textarea" name="notes" value={form.notes} onChange={handleChange} required={false} />
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit}>
                        {loadingSubmit ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : isEdit ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'حفظ' : 'Save')}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditOrgStructureAssignment;
